import { Observable, BehaviorSubject } from 'rxjs';
import { distinctUntilChanged, map } from 'rxjs/operators';

import { Cell } from './cells/cell';

export type RowStatusColor = 'blue' | 'green' | 'black' | 'red';

export interface ExpandRowView<T> {
  item?: T;
}

export interface SelectDescriptor<T> {
  selectable: boolean;
  selectedItems?: T[];
  selectionChanged?: (selected: T[]) => void;
  selectAll?: boolean;
}

export interface TableViewConfig<T> {
  columns: ColumnDescriptor<T>[];
  emptyText?: string;
  errorText?: string;
  rowStatusColorFor?: (item: T) => RowStatusColor;
  selection?: SelectDescriptor<T>;
  loadMoreItemsTriggered?: () => void;
  loadMoreButtonText?: string;
  showLoadMoreButton?: boolean;
  fixedTable?: boolean;
}

export interface ColumnConfig {
  minWidth?: number | string;
  maxWidth?: number | string;
  width?: string;
}

export type ColumnAlignment = 'right' | 'left' | 'center';

export interface ColumnDescriptor<T> {
  title: string;
  cellForItem: (item: T) => string | Cell;
  sortOn?: string;
  alignment?: ColumnAlignment;
  config?: ColumnConfig;
  hidden?: boolean;
}

export class TableViewModel<T> {
  private columns: ColumnDescriptor<T>[];
  rowStatusColorFor?: (item: T) => RowStatusColor;
  emptyText = 'No results found.';
  loadMoreItemsTriggered?: () => void;
  loadMoreButtonText = 'Load More';
  fixedTable: boolean;
  showLoadMoreButton = false;
  selection?: SelectDescriptor<T>;

  private store$ = new BehaviorSubject<TableViewConfig<T>>({ columns: [] });
  private store = this.store$.asObservable().pipe(distinctUntilChanged());

  select<I extends keyof TableViewConfig<T>>(name: I): Observable<TableViewConfig<T>[I]> {
    return this.store.pipe(map((state) => state[name]));
  }

  constructor(config: TableViewConfig<T>) {
    if (config.columns) {
      this.columns = config.columns;
    }
    if (config.emptyText) {
      this.emptyText = config.emptyText;
    }
    if (config.rowStatusColorFor) {
      this.rowStatusColorFor = config.rowStatusColorFor;
    }
    if (config.loadMoreItemsTriggered) {
      this.loadMoreItemsTriggered = config.loadMoreItemsTriggered;
    }
    if (config.loadMoreButtonText) {
      this.loadMoreButtonText = config.loadMoreButtonText;
    }
    if (config.showLoadMoreButton) {
      this.showLoadMoreButton = config.showLoadMoreButton;
    }
    if (config.fixedTable) {
      this.fixedTable = config.fixedTable;
    }
    if (config.selection) {
      this.selection = config.selection;
    }
    const value = this.store$.value;
    this.store$.next({ ...value, columns: [...value.columns, ...config.columns] });
  }

  addColumn(column: ColumnDescriptor<T>): void {
    const currentColumns = this.store$.value.columns;
    const updatedColumns = [...currentColumns, column];
    const value = this.store$.value;
    this.store$.next({ ...value, columns: updatedColumns });
  }

  columnExists(columnTitle: string): boolean {
    return this.getColumnIndexWithTitle(columnTitle) !== -1;
  }

  private getColumnIndexWithTitle(columnTitle: string): number {
    const currentColumns = this.store$.value.columns;
    for (let i = 0; i < currentColumns.length; i++) {
      if (currentColumns[i].title === columnTitle) {
        return i;
      }
    }
    return -1;
  }

  addColumnAtIndex(column: ColumnDescriptor<T>, index: number): void {
    const currentColumns = this.store$.value.columns;
    const updatedColumns = [...currentColumns.slice(0, index), column, ...currentColumns.slice(index)];

    const value = this.store$.value;
    this.store$.next({ ...value, columns: updatedColumns });
  }

  updateColumnTitled(title: string, newValue: Partial<ColumnDescriptor<T>>): void {
    const currentColumns = this.store$.value.columns;
    const updatedColumns = currentColumns.map((column) => {
      if (column.title !== title) {
        return column;
      }
      return Object.assign({}, column, newValue);
    });

    const value = this.store$.value;
    this.store$.next({ ...value, columns: updatedColumns });
  }

  updateColumnAtIndex(index: number, newValue: Partial<ColumnDescriptor<T>>): void {
    const currentColumns = this.store$.value.columns;
    const updatedColumns = currentColumns.map((column, columnIndex) => {
      if (columnIndex !== index) {
        return column;
      }
      return Object.assign({}, column, newValue);
    });

    const value = this.store$.value;
    this.store$.next({ ...value, columns: updatedColumns });
  }

  removeColumnWithTitle(title: string): void {
    const currentColumns = this.store$.value.columns;
    const deleteIndex = this.getColumnIndexWithTitle(title);

    if (deleteIndex !== -1) {
      const updatedColumns = [...currentColumns.slice(0, deleteIndex), ...currentColumns.slice(deleteIndex + 1)];

      const value = this.store$.value;
      this.store$.next({ ...value, columns: updatedColumns });
    }
  }

  removeColumnAtIndex(index: number): void {
    const currentColumns = this.store$.value.columns;
    const updatedColumns = [...currentColumns.slice(0, index), ...currentColumns.slice(index + 1)];

    const value = this.store$.value;
    this.store$.next({ ...value, columns: updatedColumns });
  }

  toggleColumnWithTitle(title: string): void {
    const currentColumns = this.store$.value.columns;
    const columnIndex = this.getColumnIndexWithTitle(title);

    if (columnIndex !== -1) {
      currentColumns[columnIndex].hidden = !currentColumns[columnIndex].hidden;
      const value = this.store$.value;
      this.store$.next({ ...value, columns: currentColumns });
    }
  }
}
